/**
 * V2 of UserProfilePage - optimized for the account settings modal.
 * Combines profile info with preferences in a single view.
 * When settings_split_enabled flag is fully rolled out, this can replace UserProfilePage.
 */
import { Avatar, AvatarFallback, AvatarImage, AvatarInitials } from "@/components/Avatar";
import { EditorIcon } from "@/components/environments/EditorIcon";
import { CopyableInput } from "@/components/podkit/forms/CopyableInput";
import { Input } from "@/components/podkit/forms/Input";
import { InputField } from "@/components/podkit/forms/InputField";
import { Label } from "@/components/podkit/forms/Label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/podkit/select/Select";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import { Heading3 } from "@/components/podkit/typography/Headings";
import { Text } from "@/components/podkit/typography/Text";
import { DeleteAccount } from "@/components/settings/profile/DeleteAccount";
import { Switch } from "@/components/Switch";
import { ThemeToggleV2 } from "@/components/ThemeToggleV2";
import { useSetDefaultEditor } from "@/hooks/use-default-editor";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useGetEditorForEnv } from "@/hooks/use-editor-for-env";
import { usePreferenceValue } from "@/hooks/use-preference-value";
import { useGetAccount, type PlainAccount } from "@/queries/account-queries";
import {
    useAuthenticatedUser,
    useGetDotfilesConfiguration,
    useSetDotfilesConfiguration,
    type PlainUser,
} from "@/queries/user-queries";
import { formatError } from "@/utils/errors";
import { useCallback, useEffect, useId, useState, type FC } from "react";

export const UserProfilePageV2: FC = () => {
    useDocumentTitle("Profile");

    const { data: account } = useGetAccount();
    const { data: user } = useAuthenticatedUser();

    return (
        <div className="flex flex-col gap-6" data-testid="user-profile-page">
            {/* Profile section */}
            <div className="flex max-w-[600px] flex-col gap-4">
                <UserIdentity user={user} account={account || undefined} />
                <AccountId account={account || undefined} />
            </div>

            <hr className="border-border-subtle" />

            {/* Preferences section */}
            <div className="flex max-w-[600px] flex-col gap-4">
                <Heading3>Preferences</Heading3>
                <Appearance />
                <DefaultEditor />
                <AgentNotifications />
                <DotfilesConfiguration />
            </div>

            <hr className="border-border-subtle" />

            {/* Danger zone - at the bottom */}
            <div className="max-w-[600px]">
                <DeleteAccount />
            </div>
        </div>
    );
};

const UserIdentity: FC<{ user?: PlainUser; account?: PlainAccount }> = ({ user, account }) => {
    return (
        <div className="flex items-center gap-4">
            <Avatar size={48} className="border border-border-base">
                {user?.avatarUrl && <AvatarImage src={user.avatarUrl} alt={`${user.name || "User"}'s avatar`} />}
                <AvatarFallback>
                    <AvatarInitials name={user?.name || "User"} size={48} />
                </AvatarFallback>
            </Avatar>
            <div className="flex flex-col">
                <span className="text-base font-medium text-content-primary" data-testid="name-input">
                    {user?.name}
                </span>
                <span className="text-base text-content-strong" data-testid="email-input">
                    {account?.email}
                </span>
            </div>
        </div>
    );
};

const AccountId: FC<{ account?: PlainAccount }> = ({ account }) => {
    const id = useId();

    return (
        <InputField label="Account ID" id={id}>
            <div className="max-w-[600px]">
                <CopyableInput
                    id={id}
                    title="Copy account ID"
                    className="w-full"
                    value={account?.id ?? ""}
                    copyable
                    disabled
                    data-tracking-id="copy-account-id-user-profile-page"
                />
            </div>
        </InputField>
    );
};

const Appearance: FC = () => {
    return (
        <InputField label="Appearance" hint="Customize how Ona looks on your device" id="appearance">
            <ThemeToggleV2 />
        </InputField>
    );
};

const DefaultEditor: FC = () => {
    const setDefaultEditor = useSetDefaultEditor();
    const { selectedEditor, availableEditors, isPending: isPendingEditors } = useGetEditorForEnv(undefined);

    return (
        <InputField
            label="Default Editor"
            hint={
                <span>
                    This will be your default selected editor for environments.{" "}
                    <ExternalLink href={"https://ona.com/docs/ona/editors/overview"} className="text-sm">
                        Learn more
                    </ExternalLink>
                    .
                </span>
            }
        >
            <Select
                name="defaultEditor"
                value={selectedEditor?.id}
                onValueChange={(editorId) => setDefaultEditor(editorId)}
            >
                <SelectTrigger loading={isPendingEditors}>
                    <SelectValue>
                        <div className="flex items-center gap-2">
                            <EditorIcon editor={selectedEditor!} size="base" />
                            <span>{selectedEditor?.name}</span>
                        </div>
                    </SelectValue>
                </SelectTrigger>
                <SelectContent>
                    {(availableEditors || []).map((editor) => (
                        <SelectItem key={editor.id} value={editor.id}>
                            <div className="flex items-center gap-2">
                                <EditorIcon editor={editor} size="base" />
                                <span>{editor.name}</span>
                            </div>
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </InputField>
    );
};

const AgentNotifications: FC = () => {
    const { value: agentNotificationValue, setValue: setAgentNotificationValue } =
        usePreferenceValue("agent-done-notification");

    const isEnabled = agentNotificationValue === "sound";

    const handleToggle = useCallback(
        (checked: boolean) => {
            setAgentNotificationValue(checked ? "sound" : "none");
        },
        [setAgentNotificationValue],
    );

    return (
        <div className="flex flex-col gap-3">
            <div className="flex gap-4">
                <div className="flex">
                    <Switch
                        state={isEnabled ? "checked" : "unchecked"}
                        onToggle={handleToggle}
                        id="agent-done-notification-toggle"
                        aria-label="Play sound when agent is done"
                    />
                </div>
                <div className="flex flex-col gap-3">
                    <div className="flex flex-col gap-1">
                        <Label className="text-base">Agent done notification</Label>
                        <Text className="text-sm text-content-secondary">Play sound when agent is done</Text>
                    </div>
                </div>
            </div>
        </div>
    );
};

const DotfilesConfiguration: FC = () => {
    const { data: dotfilesConfiguration, isPending: isPendingDotfilesConfiguration } = useGetDotfilesConfiguration();
    const setDotfilesConfiguration = useSetDotfilesConfiguration();
    const { toast } = useToast();

    const [dotfilesRepositoryError, setDotfilesRepositoryError] = useState<string | null>(null);
    const [dotfilesRepository, setDotfilesRepository] = useState("");
    const [savedDotfilesRepository, setSavedDotfilesRepository] = useState("");
    useEffect(() => {
        if (isPendingDotfilesConfiguration) {
            return;
        }
        setDotfilesRepository(dotfilesConfiguration?.repository || "");
        setSavedDotfilesRepository(dotfilesConfiguration?.repository || "");
    }, [dotfilesConfiguration?.repository, isPendingDotfilesConfiguration]);

    const updateDotfilesConfiguration = useCallback(
        async (repositoryUrl: string) => {
            if (savedDotfilesRepository === repositoryUrl) {
                return;
            }

            try {
                await setDotfilesConfiguration.mutateAsync({ repositoryUrl });
                setSavedDotfilesRepository(repositoryUrl);
                toast({ title: "Dotfiles repository updated" });
            } catch (error) {
                toast({
                    title: "Failed to update dotfiles repository",
                    description: formatError(error),
                });
            }
        },
        [savedDotfilesRepository, setDotfilesConfiguration, toast],
    );

    return (
        <InputField
            label="Dotfiles repository"
            error={dotfilesRepositoryError}
            hint={
                <span>
                    URL of a dotfiles Git repository.{" "}
                    <ExternalLink href={"https://ona.com/docs/ona/configuration/dotfiles/overview"} className="text-sm">
                        Learn more
                    </ExternalLink>
                    .
                </span>
            }
        >
            <Input
                disabled={isPendingDotfilesConfiguration || setDotfilesConfiguration.isPending}
                value={dotfilesRepository}
                type="url"
                placeholder="https://github.com/your-username/dotfiles"
                onChange={(e) => setDotfilesRepository(e.target.value)}
                data-testid="dotfiles-configuration-input"
                onBlur={(e) => {
                    if (e.target instanceof HTMLInputElement) {
                        const isValid = e.target.checkValidity();
                        setDotfilesRepositoryError(isValid ? null : "Invalid repository URL");
                        if (isValid) {
                            void updateDotfilesConfiguration(dotfilesRepository ?? "");
                        }
                    }
                }}
            />
        </InputField>
    );
};
